import { ensureDefined, getFromWindowRef, HpQueryValueMap, SMap } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import { HpError } from '@avantia/logging-and-errors';
import { destructureControlId } from '@avantia/questionset-model';
import jQuery from 'jquery';
import { BaseSyntheticEvent } from 'react';
import { hpClaimsAlertEventType } from '../actions/eventActions';
import { HpClaimsDebugFlags, logInfo } from '../clientLogging';
import { HpClaimsClientConfiguration } from '../config/configurationTypes';
import { triggerGeneralActionEvent, triggerPageLoadEvent } from '../engineClient/actions/eventActions';
import { createPageComponentFunctions } from '../engineClient/components/pageComponentUtilities';
import { registerConfigurationAction } from '../engineClient/config/registerClientConfig';
import { pageLoadDataHandler } from '../engineClient/handlers/pageLoadDataHandler';
import { getMetaDataOrUndefinedFromAnswers } from '../engineClient/tools/clientAndServerUtilities';
import { getProductIdCookie, setAnswersIdCookie, setProductIdCookie } from '../engineClient/tools/cookieUtilities';
import { HpClientQuestion } from '../engineClient/types';
import { HpQuestionSetPageProps } from '../engineClient/types/questionSetPagePropTypes';
import { multiplePoliciesMode } from '../clientMockData';
import {
  getIsHolmesEnabledClientCookie,
  getMockPolicyDataModeCookie,
  getShouldSimulateSiteViewErrorClientCookie,
  isLiveEnvironment,
  setIsHolmesEnabledCookie,
  setMockPolicyDataModeCookie,
  setShouldSimulateSiteViewErrorCookie
} from '../handlers/handlerUtilities';
import { customerDetailsKeyName, mockDataModeHashName } from '../handlers/handlerTypes';
import { HpNeedAnswerTypes } from '../types/pageLoadDataTypes';
import { hpClaimsAlertControlClass } from './formControls/custom';
import { HpClaimsQuestionSetPageProps } from './questionSetForm/questionSetPageTypes';

let productIdDefault: string, websiteEndpoint: string, questionSetPagePath: string;

registerConfigurationAction((config) => {
  ({ productIdDefault, websiteEndpoint } = config as HpClaimsClientConfiguration);
  questionSetPagePath = `${websiteEndpoint}/`;
});

export type HpPageType = 'error' | 'questions';

const allPageTypes: HpPageType[] = ['error'];

export function runLoadDataHandler(props: HpQuestionSetPageProps): void {
  const {
    answersId: answersIdLoaded,
    location,
    history,
    displayState,
    answers,
    productId,
    getAnswers,
    getQuestionSet,
    getQuestionSets,
    claimJourneyComplete
  } = props as HpClaimsQuestionSetPageProps;
  if (claimJourneyComplete) {
    history.push('/confirmation');
  }

  const loadDataResult = pageLoadDataHandler({
    answersIdLoaded,
    location,
    productId,
    questionSetsLoadedCount: displayState.availableQuestionSets.items.length
  });

  const { pathname: path } = location;
  const query = new URLSearchParams(location.search);

  if (!isLiveEnvironment() && location.hash.includes(mockDataModeHashName)) {
    if (!getMockPolicyDataModeCookie()) {
      setMockPolicyDataModeCookie({ mode: multiplePoliciesMode });
    }

    const { location: windowLocation } = getFromWindowRef<{ location: Location }>(['location']) || {};

    if (windowLocation) {
      windowLocation.replace(`${path}?${customerDetailsKeyName}=1`);
    }

    return;
  }

  const queryFlags = getClaimsQueryFlags(query);
  const claimsCookieSetters = getClaimsCookieSetters(queryFlags);

  setClaimsCookies(claimsCookieSetters);

  if (loadDataResult) {
    const { load, answersId, needs: needsString, productId, newUrl, setCustomAnswers } = loadDataResult;
    const needs = needsString as HpNeedAnswerTypes;
    if (needs === 'generate-id') {
      setAnswersIdCookie('');
    }

    const oldLocation = { ...location };
    if (newUrl !== undefined) {
      history.push(newUrl);
      logInfo({ message: `Pushed to history: '${newUrl}'` }, HpClaimsDebugFlags.PageLoadDataHandler);
    }

    if (load !== 'none') {
      if (path === questionSetPagePath) {
        const hash: string | null = oldLocation.hash || null;
        triggerPageLoadEvent({ hash, path, gtmTag: null });
      }

      let prodId = productId;
      if (!prodId) {
        const productCookieValue = getProductIdCookie();
        prodId = productCookieValue || productIdDefault;
        if (prodId !== productCookieValue) {
          setProductIdCookie(prodId);
        }
      }

      const metadata = getMetaDataOrUndefinedFromAnswers(prodId, answers);

      if (load === 'all-qsets') {
        getQuestionSets({ excludeProductId: productIdDefault });
      } else if (load === 'qset-only') {
        let queryParams: HpQueryValueMap | undefined;
        if (needs === 'policy-data') {
          const customerDetailsKey = query.get(customerDetailsKeyName) || undefined;
          const emailAddress = query.get('emailAddress') || undefined;
          const postcode = query.get('postcode') || undefined;

          queryParams = { customerDetailsKey, generateAnswersGuid: true, emailAddress, postcode };
        }

        getQuestionSet({
          productId: prodId,
          needs,
          queryParams,
          metadata,
          setCustomAnswers
        });
      } else {
        getAnswers({
          answersId: answersId as string,
          needs,
          productId: prodId,
          customHandler: undefined,
          queryParams: undefined,
          metadata,
          setCustomAnswers: ensureDefined(setCustomAnswers, 'setCustomAnswers')
        });
      }
    }
  }
}

export function createClaimsPageComponentFunctions(): void {
  createPageComponentFunctions<HpPageType>({ allPageTypes, runLoadDataHandler });
}

export const maybeTriggerClaimAlertEvents: MaybeTriggerClaimAlertEventsFunction = (questions) => {
  if (!Object.keys(questions).length) {
    return;
  }

  const alertElements = jQuery(`.${hpClaimsAlertControlClass}`);
  for (const alert of alertElements) {
    const isVisible = $(alert).is(':visible');
    if (isVisible) {
      const event = { target: alert, type: hpClaimsAlertEventType } as BaseSyntheticEvent;
      const { id } = alert;
      const { questionId } = destructureControlId(id);
      const question = questions[questionId];
      if (!question) {
        throw new HpError(`Question ${questionId} does not exist. Alerts must have a valid question id.`);
      }

      const { gtmTag } = question;
      triggerGeneralActionEvent({ getEvent: () => event, gtmTag: gtmTag || '' });
    }
  }
};

type MaybeTriggerClaimAlertEventsFunction = (questions: SMap<HpClientQuestion>) => void;

export const maybeTriggerClaimAlertEventsWithDelay: MaybeTriggerClaimAlertEventsFunction = lodash.debounce(
  maybeTriggerClaimAlertEvents,
  350
);

interface HpClaimsQueryFlags {
  readonly simulateSiteViewError: boolean;
  readonly isHolmesEnabled: boolean;
}

interface HpCookieSetter {
  readonly getter: () => boolean;
  readonly setter: (value: boolean) => void;
  readonly value: boolean;
}

function getClaimsQueryFlags(query: URLSearchParams): HpClaimsQueryFlags {
  return {
    simulateSiteViewError: query.has('simulateSiteViewError'),
    isHolmesEnabled: query.has('isHolmesEnabled')
  };
}

function getClaimsCookieSetters(queryFlags: HpClaimsQueryFlags): readonly HpCookieSetter[] {
  return [
    {
      getter: getShouldSimulateSiteViewErrorClientCookie,
      setter: setShouldSimulateSiteViewErrorCookie,
      value: queryFlags.simulateSiteViewError
    },
    {
      getter: getIsHolmesEnabledClientCookie,
      setter: setIsHolmesEnabledCookie,
      value: queryFlags.isHolmesEnabled
    }
  ];
}

function setClaimsCookies(cookieSetters: readonly HpCookieSetter[]): void {
  cookieSetters.forEach(({ getter, setter, value }) => {
    if (getter() !== true) {
      setter(value);
    }
  });
}
